// /admin/docs — Dashboard de documentación interna.
// Los manuales viven en bucket privado 'docs-internos' (Supabase Storage).
// El botón Abrir / Descargar llama la edge function 'get-doc-url' que valida
// que el user sea admin y devuelve una signed URL de 5 min.
//
// Hasta el 25-may-2026 los archivos estaban en public/docs-share/ y eran
// accesibles por URL pública sin login. Se migró a Storage privado por seguridad.

import { useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const DOCS = [
  {
    id: 'mapa',
    file: '00-Mapa-del-Proyecto',
    titulo: 'Mapa del Proyecto',
    descripcion: 'Overview completo: URLs, stack, base de datos, pantallas, equipo, decisiones activas. El punto de entrada de toda la documentación.',
    icono: '🗺️',
    audiencia: 'Todos',
    color: '#1A3461',
  },
  {
    id: 'marca',
    file: '01-Manual-de-Marca',
    titulo: 'Manual de Marca',
    descripcion: 'Identidad oficial: paleta v2.0, tipografía Inter, logo, voz rioplatense, mensajes clave, reglas anti-error, vertical Empresas. Fuente única para cualquier pieza.',
    icono: '🎨',
    audiencia: 'Marketing · diseño · partners',
    color: '#5BBDD8',
  },
  {
    id: 'admin',
    file: '02-Manual-del-Admin',
    titulo: 'Manual del Admin',
    descripcion: 'Cómo usar el panel admin: pantalla por pantalla con quién la usa (Gimena, Roberto, Nico), acciones típicas y el día de trabajo típico.',
    icono: '🛠️',
    audiencia: 'Equipo (Gimena · Roberto · Nico)',
    color: '#00A8B5',
  },
  {
    id: 'runbook',
    file: '03-Runbook-Operativo',
    titulo: 'Runbook operativo',
    descripcion: 'Manual de crisis. Qué hacer cuando algo se rompe: entrega fallida, cobro rechazado, repartidor caído, sistema caído. Síntoma → causa → pasos.',
    icono: '🆘',
    audiencia: 'Todos los admins',
    color: '#DC2626',
  },
  {
    id: 'repartidor',
    file: '04-Manual-del-Repartidor',
    titulo: 'Manual del Repartidor',
    descripcion: 'Móvil-first. Cómo arrancar la ruta, marcar entregas (foto), gestionar no-entregas, contactar clientes. Pensado para abrir desde el celular en la calle.',
    icono: '🚐',
    audiencia: 'Repartidores',
    color: '#00A8B5',
  },
  {
    id: 'cliente',
    file: '05-Manual-del-Cliente',
    titulo: 'Manual del Cliente',
    descripcion: 'Cómo funciona Limpack para el cliente: empezar, Mi Plan, entrega, cobros MP suscripción, tienda, pausar/cancelar, vecinos. Base para una futura sección /ayuda en el panel.',
    icono: '👤',
    audiencia: 'Soporte · clientes (futura /ayuda)',
    color: '#1A3461',
  },
  {
    id: 'brief-roberto',
    file: '06-Brief-Infraestructura-Roberto',
    titulo: 'Brief Infraestructura ($50/mes)',
    descripcion: 'Propuesta para Roberto: pasar Supabase + Cloudflare a planes pagos antes del lanzamiento. Backups, estabilidad, qué se gana y cuánto cuesta.',
    icono: '💼',
    audiencia: 'Roberto · Nico',
    color: '#F59E0B',
  },
  {
    id: 'onboarding-tecnico',
    file: '07-Manual-Tecnico-Onboarding',
    titulo: 'Onboarding Técnico (dev nuevo)',
    descripcion: 'Manual para que un dev nuevo arme el entorno y entienda el stack en una tarde. Setup local, repo, Supabase, Cloudflare, deploys, convenciones, troubleshooting.',
    icono: '🧑‍💻',
    audiencia: 'Devs (Nico · contratistas futuros)',
    color: '#7C3AED',
  },
  {
    id: 'logos',
    file: '08-Manual-de-Logos',
    titulo: 'Manual de Logos',
    descripcion: 'Cada variante del logo con preview, archivo, peso, fondo correcto y botón de descarga directa. Para que cualquiera arme una pieza sin equivocarse de logo.',
    icono: '💧',
    audiencia: 'Quien arme piezas (Nico · Gimena · diseño externo)',
    color: '#00A8B5',
  },
  {
    id: 'finanzas-roberto',
    file: '09-Manual-Finanzas-Roberto',
    titulo: 'Manual de Finanzas (Roberto)',
    descripcion: 'Cómo funciona cada pantalla del módulo Finanzas — para que Roberto trabaje con confianza y reporte cualquier cosa que vea mal. Incluye reglas UY (IVA, IRAE, BPS), rutina mensual y formulario de feedback.',
    icono: '📒',
    audiencia: 'Roberto · Nico',
    color: '#1A3461',
  },
]

export default function Docs() {
  const [loadingId, setLoadingId] = useState(null)
  const [errorMsg, setErrorMsg] = useState(null)

  // Pide signed URL a la edge function get-doc-url y la abre / descarga.
  // path = '00-Mapa-del-Proyecto.html' o 'pdf/00-Mapa-del-Proyecto.pdf'.
  async function obtenerUrl(path) {
    const { data, error } = await supabase.functions.invoke('get-doc-url', {
      body: { path },
    })
    if (error) throw new Error(error.message || 'No se pudo obtener la URL del documento')
    if (!data?.url) throw new Error('La función no devolvió URL')
    return data.url
  }

  async function abrirHtml(file) {
    setErrorMsg(null)
    setLoadingId(`html:${file}`)
    try {
      const url = await obtenerUrl(`${file}.html`)
      // Supabase Storage agrega Content-Disposition: attachment a los HTML por
      // seguridad anti-XSS, lo que hace que el browser muestre el código
      // en lugar de renderizarlo. Workaround: bajamos el HTML y lo abrimos
      // como blob URL con type: 'text/html' explícito.
      const resp = await fetch(url)
      if (!resp.ok) throw new Error(`HTTP ${resp.status} al bajar el HTML`)
      const html = await resp.text()
      const blob = new Blob([html], { type: 'text/html; charset=utf-8' })
      const blobUrl = URL.createObjectURL(blob)
      const win = window.open(blobUrl, '_blank', 'noopener,noreferrer')
      if (!win) {
        // Popup bloqueado — fallback: link click
        const a = document.createElement('a')
        a.href = blobUrl
        a.target = '_blank'
        a.rel = 'noopener noreferrer'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      }
      // Cleanup del blob URL después de 1 min (suficiente para que el tab cargue)
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000)
    } catch (err) {
      setErrorMsg(`No se pudo abrir el manual: ${err.message}`)
    } finally {
      setLoadingId(null)
    }
  }

  async function descargarPdf(file) {
    setErrorMsg(null)
    setLoadingId(`pdf:${file}`)
    try {
      const url = await obtenerUrl(`pdf/${file}.pdf`)
      const a = document.createElement('a')
      a.href = url
      a.download = `${file}.pdf`
      a.target = '_blank'
      a.rel = 'noopener noreferrer'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    } catch (err) {
      setErrorMsg(`No se pudo descargar el PDF: ${err.message}`)
    } finally {
      setLoadingId(null)
    }
  }

  return (
    <AdminLayout title="Documentación">
      <Estilos />
      <div className="docs-wrap">
        <header className="docs-head">
          <h1>📚 Documentación de Limpack</h1>
          <p>Los manuales viven acá adentro y se actualizan junto con el sistema. Para tenerlos offline (por si el sistema se cae), descargá los PDFs y guardalos en tu Drive marcando "Disponible sin conexión".</p>
        </header>

        <div className="docs-banner-tip">
          <strong>🔒 Storage privado</strong>
          <p>Los manuales ahora viven en un bucket privado de Supabase. El acceso es admin-only y cada apertura genera una URL firmada de 5 min. Las URLs viejas <code>/docs-share/*</code> dejaron de funcionar.</p>
        </div>

        {errorMsg && (
          <div className="docs-banner-error">
            <strong>⚠ {errorMsg}</strong>
            <p>Si el problema persiste, avisame. Mientras tanto, los archivos fuente están en <code>docs-share/</code> del repo del proyecto.</p>
          </div>
        )}

        <div className="docs-grid">
          {DOCS.map(d => (
            <div key={d.id} className="doc-card" style={{ borderTopColor: d.color }}>
              <div className="doc-icono" style={{ background: `${d.color}15`, color: d.color }}>
                {d.icono}
              </div>
              <h3 className="doc-titulo">{d.titulo}</h3>
              <div className="doc-audiencia">{d.audiencia}</div>
              <p className="doc-descripcion">{d.descripcion}</p>
              <div className="doc-acciones">
                <button
                  type="button"
                  className="doc-btn doc-btn-primary"
                  onClick={() => abrirHtml(d.file)}
                  disabled={loadingId === `html:${d.file}`}
                >
                  {loadingId === `html:${d.file}` ? '…' : 'Abrir →'}
                </button>
                <button
                  type="button"
                  className="doc-btn doc-btn-secondary"
                  onClick={() => descargarPdf(d.file)}
                  disabled={loadingId === `pdf:${d.file}`}
                >
                  {loadingId === `pdf:${d.file}` ? '…' : '⬇ PDF'}
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="docs-tip">
          <strong>💡 Cómo mantener un backup offline:</strong>
          <ol>
            <li>Bajate los PDFs de los manuales críticos (sobre todo el <strong>Runbook</strong>).</li>
            <li>Subilos a tu carpeta de Google Drive personal.</li>
            <li>En el Drive marcalos como "Disponible sin conexión" — quedan en el celular.</li>
            <li>Para el Runbook específicamente: imprimí una copia física y dejala en oficina + casa. Es lo que vas a leer si todo se cae.</li>
          </ol>
        </div>
      </div>
    </AdminLayout>
  )
}

function Estilos() {
  return (
    <style>{`
      .docs-wrap { max-width: 1080px; margin: 0 auto; padding: 24px; font-family: Inter, system-ui, sans-serif; color: #1A3461; }

      .docs-banner-tip {
        background: #F0FDFA; border-left: 4px solid #00A8B5;
        padding: 12px 18px; border-radius: 0 12px 12px 0;
        margin-bottom: 16px; color: #134E4A;
      }
      .docs-banner-tip strong { display: block; font-size: 14px; margin-bottom: 3px; color: #1A3461; }
      .docs-banner-tip p { font-size: 13px; margin: 0; line-height: 1.5; }
      .docs-banner-tip code { background: rgba(0,168,181,0.12); padding: 1px 6px; border-radius: 4px; font-size: 12px; }

      .docs-banner-error {
        background: #FEE2E2; border-left: 4px solid #DC2626;
        padding: 12px 18px; border-radius: 0 12px 12px 0;
        margin-bottom: 16px; color: #991B1B;
      }
      .docs-banner-error strong { display: block; font-size: 14px; margin-bottom: 3px; color: #991B1B; }
      .docs-banner-error p { font-size: 13px; margin: 0; line-height: 1.5; }
      .docs-banner-error code { background: rgba(220,38,38,0.12); padding: 1px 6px; border-radius: 4px; font-size: 12px; }

      .doc-btn:disabled { opacity: 0.5; cursor: wait; }

      .docs-head { margin-bottom: 24px; }
      .docs-head h1 { font-size: 26px; font-weight: 900; margin: 0 0 6px; letter-spacing: -0.02em; }
      .docs-head p { font-size: 14px; color: #64748B; margin: 0; line-height: 1.55; max-width: 720px; }

      .docs-grid { display: grid; grid-template-columns: 1fr; gap: 14px; margin-bottom: 24px; }
      @media (min-width: 720px) { .docs-grid { grid-template-columns: 1fr 1fr; } }
      @media (min-width: 1024px) { .docs-grid { grid-template-columns: 1fr 1fr 1fr; } }

      .doc-card {
        background: #fff; border: 1px solid #E2E8F0; border-top: 4px solid #1A3461;
        border-radius: 14px; padding: 20px;
        display: flex; flex-direction: column; gap: 8px;
        transition: transform 0.15s, box-shadow 0.15s;
      }
      .doc-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(15,23,42,0.08); }

      .doc-icono {
        width: 48px; height: 48px; border-radius: 12px;
        display: flex; align-items: center; justify-content: center;
        font-size: 26px; margin-bottom: 4px;
      }
      .doc-titulo { font-size: 17px; font-weight: 800; color: #1A3461; margin: 0; letter-spacing: -0.01em; }
      .doc-audiencia { font-size: 11px; font-weight: 600; color: #64748B; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 6px; }
      .doc-descripcion { font-size: 13.5px; color: #475569; line-height: 1.55; margin: 0; flex: 1; }
      .doc-acciones { display: grid; grid-template-columns: 1fr auto; gap: 8px; margin-top: 12px; }
      .doc-btn {
        padding: 9px 12px; border-radius: 8px;
        font-weight: 700; font-size: 13px; cursor: pointer;
        font-family: inherit; border: none;
        display: flex; align-items: center; justify-content: center; gap: 4px;
        transition: all 0.15s;
      }
      .doc-btn-primary { background: #1A3461; color: #fff; }
      .doc-btn-primary:hover { background: #1A3461; }
      .doc-btn-secondary { background: #fff; border: 1.5px solid #E2E8F0; color: #475569; }
      .doc-btn-secondary:hover { border-color: #1A3461; color: #1A3461; }

      .docs-tip {
        background: #F0FDFA; border-left: 4px solid #00A8B5;
        padding: 16px 20px; border-radius: 0 12px 12px 0;
        font-size: 14px; color: #134E4A;
      }
      .docs-tip strong { color: #1A3461; display: block; margin-bottom: 8px; font-size: 14.5px; }
      .docs-tip ol { margin: 0; padding-left: 22px; }
      .docs-tip li { margin: 4px 0; }
    `}</style>
  )
}
